import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  Code, 
  Brain, 
  Cpu, 
  Calendar, 
  Users, 
  Target,
  CheckCircle,
  Smartphone,
  Settings
} from "lucide-react";
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  SecondaryButton, 
  Badge
} from "../styles/GlobalStyles";

// Career Paths Section
const CareerPathsSection = styled(Section)`
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--primary-50) 50%, var(--secondary-50) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
    animation: float 20s ease-in-out infinite;
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  .badge {
    margin-bottom: var(--space-4);
  }
  
  h1 {
    font-size: var(--text-6xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 1024px) {
      font-size: var(--text-5xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-3xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 700px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      padding: 0 var(--space-4);
    }
  }
`;

const PathSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-12);
  }
`;

const PathTab = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  border: 2px solid transparent;
  border-radius: var(--radius-xl);
  background: ${props => props.active ? 'var(--primary-500)' : 'var(--gray-0)'};
  color: ${props => props.active ? 'var(--gray-0)' : 'var(--gray-700)'};
  font-weight: 600;
  font-size: var(--text-base);
  transition: all var(--transition-normal);
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
    justify-content: center;
    padding: var(--space-3) var(--space-5);
  }
  
  &:hover {
    border-color: var(--primary-500);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    
    @media (max-width: 768px) {
      transform: translateY(-1px);
    }
  }
`;

const PathContent = styled.div`
  margin-bottom: var(--space-20);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-16);
  }
`;

const PathHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-12);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-8);
  }
  
  h2 {
    font-size: var(--text-4xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-2xl);
    }
  }
  
  p {
    font-size: var(--text-lg);
    color: var(--gray-600);
    max-width: 600px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: var(--text-base);
      padding: 0 var(--space-4);
    }
  }
`;

const YearGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
`;

const YearCard = styled(Card)`
  padding: var(--space-8);
  border: 2px solid transparent;
  background: var(--gray-0);
  position: relative;
  transition: all var(--transition-normal);
  
  @media (max-width: 768px) {
    padding: var(--space-6);
  }
  
  &:hover {
    border-color: var(--primary-200);
    transform: translateY(-8px);
    box-shadow: var(--shadow-2xl);
    
    @media (max-width: 768px) {
      transform: translateY(-4px);
    }
  }
  
  .year-badge {
    position: absolute;
    top: -15px;
    left: var(--space-6);
    background: var(--gradient-primary);
    color: var(--gray-0);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-full);
    font-weight: 600;
    font-size: var(--text-sm);
  }
  
  .year-header {
    margin-top: var(--space-4);
    margin-bottom: var(--space-6);
    
    h3 {
      font-size: var(--text-2xl);
      color: var(--gray-900);
      margin-bottom: var(--space-2);
      font-weight: 700;
      
      @media (max-width: 768px) {
        font-size: var(--text-xl);
      }
    }
    
    p {
      color: var(--gray-600);
      font-size: var(--text-base);
      
      @media (max-width: 768px) {
        font-size: var(--text-sm);
      }
    }
  }
  
  .skills-list {
    margin-bottom: var(--space-6);
    
    h4 {
      font-size: var(--text-lg);
      color: var(--gray-900);
      margin-bottom: var(--space-4);
      font-weight: 600;
      
      @media (max-width: 768px) {
        font-size: var(--text-base);
      }
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) 0;
        color: var(--gray-600);
        font-size: var(--text-sm);
        
        @media (max-width: 768px) {
          font-size: var(--text-xs);
        }
        
        .check-icon {
          color: var(--secondary-500);
          width: 16px;
          height: 16px;
        }
      }
    }
  }
  
  .year-stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-6);
    padding: var(--space-4);
    background: var(--gray-50);
    border-radius: var(--radius-lg);
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: var(--space-2);
      text-align: center;
    }
    
    .stat {
      text-align: center;
      
      @media (max-width: 768px) {
        text-align: center;
      }
      
      .value {
        font-size: var(--text-2xl);
        font-weight: 700;
        color: var(--primary-600);
        display: block;
        
        @media (max-width: 768px) {
          font-size: var(--text-xl);
        }
      }
      
      .label {
        font-size: var(--text-xs);
        color: var(--gray-600);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }
`;

const CtaSection = styled.div`
  text-align: center;
  background: var(--gradient-primary);
  border-radius: var(--radius-3xl);
  padding: var(--space-16) var(--space-8);
  color: var(--gray-0);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-6);
  }
  
  @media (max-width: 480px) {
    padding: var(--space-8) var(--space-4);
  }
  
  h2 {
    font-size: var(--text-4xl);
    margin-bottom: var(--space-4);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-2xl);
    }
  }
  
  p {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      margin-bottom: var(--space-6);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-base);
    }
  }
  
  .cta-buttons {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }
  }
`;

const WhiteButton = styled(SecondaryButton)`
  background: var(--gray-0);
  color: var(--primary-600);
  border: 2px solid var(--gray-0);
  
  &:hover {
    background: var(--primary-50);
    border-color: var(--gray-0);
  }
`;

// Career Paths Data
const careerPaths = {
  aiready: {
    title: "AI-Ready Job Path (18 Months)",
    description: "Fast-track to AI career with GenAI, LLMs, Full-Stack & DSA. Job guarantee program!",
    icon: <Brain size={24} />,
    years: [
      {
        year: "Phase 1",
        title: "Foundation & DSA Mastery",
        description: "Programming fundamentals with strong problem-solving skills",
        duration: "6 months",
        projects: "8+ Projects",
        skills: [
          "Python/Java Programming Advanced",
          "Data Structures & Algorithms (500+ problems)",
          "System Design Basics",
          "Git, GitHub, Development Tools",
          "Object-Oriented Programming",
          "Database Fundamentals (SQL/NoSQL)",
          "API Development Basics",
          "Mock Interviews & Coding Practice"
        ]
      },
      {
        year: "Phase 2",
        title: "AI & Full-Stack Development",
        description: "Modern AI development with full-stack web applications",
        duration: "6 months",
        projects: "12+ Projects",
        skills: [
          "GenAI & LLM Development (OpenAI, Anthropic)",
          "RAG Systems & Vector Databases",
          "MERN/MEAN Stack (React, Node.js)",
          "Machine Learning with Python (TensorFlow)",
          "AI-Powered Web Applications",
          "Cloud Deployment (AWS, Vercel)",
          "DevOps Basics (Docker, CI/CD)",
          "Real-time AI Applications"
        ]
      },
      {
        year: "Phase 3",
        title: "Job Placement & Industry Experience",
        description: "Guaranteed job placement with comprehensive support",
        duration: "6 months",
        projects: "5+ Enterprise Projects",
        skills: [
          "Advanced Interview Preparation",
          "Technical & Behavioral Interview Mastery",
          "Portfolio Optimization & Personal Branding",
          "LinkedIn & Professional Networking",
          "Salary Negotiation Skills",
          "Industry Mentorship Program",
          "Direct Company Referrals",
          "Career Growth Planning"
        ]
      }
    ]
  },
  fullstack: {
    title: "AI-Enhanced Full-Stack Development",
    description: "Master full-stack development with AI integration, .NET, and mobile technologies for the future",
    icon: <Code size={24} />,
    years: [
      {
        year: "Year 1",
        title: "Modern Frontend & AI Fundamentals",
        description: "Build responsive frontends with AI-powered features",
        duration: "12 months",
        projects: "10+ AI-Integrated Projects",
        skills: [
          "HTML5, CSS3, JavaScript ES6+ & TypeScript",
          "React.js, Next.js, Vue.js with AI Components",
          "AI-Powered UI/UX (ChatGPT Integration)",
          "Responsive & Progressive Web Apps",
          "Git Version Control & Team Collaboration",
          "RESTful APIs & GraphQL",
          "Database Fundamentals (SQL/NoSQL)",
          "Web Performance & SEO Optimization"
        ]
      },
      {
        year: "Year 2",
        title: "Backend, Mobile & AI Integration",
        description: "Full-stack backend development with mobile apps and AI services",
        duration: "12 months",
        projects: "15+ Cross-Platform Projects",
        skills: [
          "Node.js, Express.js & .NET Core Development",
          "Python Django/Flask with AI Libraries",
          "React Native & Flutter Mobile Development",
          "Cross-Platform App Development",
          "AI API Integration (OpenAI, Claude, Gemini)",
          "Cloud Services (AWS, Azure, Google Cloud)",
          "Microservices & Docker Containerization",
          "Advanced Database Design & Optimization"
        ]
      },
      {
        year: "Year 3",
        title: "AI-Driven Architecture & Leadership",
        description: "Lead AI-powered full-stack projects and scalable systems",
        duration: "12 months",
        projects: "20+ Enterprise-Grade AI Projects",
        skills: [
          "AI-Driven System Architecture & Design",
          "Enterprise .NET Applications with AI",
          "Advanced Mobile App Architecture",
          "LLM Integration & Custom AI Models",
          "DevOps, CI/CD & AI Model Deployment",
          "Performance Monitoring & Analytics",
          "Team Leadership & Code Review",
          "AI Product Strategy & Innovation"
        ]
      }
    ]
  },
  mobile: {
    title: "AI-Powered Mobile Development",
    description: "Create intelligent mobile apps with AI capabilities, cross-platform expertise, and future-ready skills",
    icon: <Target size={24} />,
    years: [
      {
        year: "Year 1",
        title: "Mobile Fundamentals & AI Basics",
        description: "Master mobile development with AI integration from day one",
        duration: "12 months",
        projects: "12+ Smart Mobile Apps",
        skills: [
          "Java/Kotlin for Android & Swift for iOS",
          "React Native & Flutter Cross-Platform",
          "Mobile UI/UX Design Principles",
          "AI-Powered Features (Voice, Vision, Text)",
          "Mobile Database & Storage Solutions",
          "REST API Integration & Real-time Data",
          "Mobile Security & Authentication",
          "App Store Deployment & Optimization"
        ]
      },
      {
        year: "Year 2",
        title: "Advanced Mobile AI & Backend",
        description: "Build sophisticated AI-powered mobile solutions",
        duration: "12 months",
        projects: "18+ AI-Native Mobile Apps",
        skills: [
          "On-Device AI & Machine Learning (TensorFlow Lite)",
          "Computer Vision & ARKit/ARCore Integration",
          "Natural Language Processing in Mobile",
          "Backend Services (.NET, Node.js) for Mobile",
          "Real-time Communication (WebSocket, WebRTC)",
          "Cloud AI Services Integration",
          "Advanced State Management & Architecture",
          "Performance Optimization & Testing"
        ]
      },
      {
        year: "Year 3",
        title: "Enterprise Mobile AI Solutions",
        description: "Lead mobile AI innovation and enterprise app development",
        duration: "12 months",
        projects: "25+ Enterprise Mobile AI Solutions",
        skills: [
          "Custom AI Model Integration in Mobile",
          "Enterprise Mobile App Architecture",
          "Advanced AR/VR & Metaverse Development",
          "Blockchain & Web3 Mobile Integration",
          "IoT Mobile App Development",
          "Mobile DevOps & Automated Testing",
          "Team Leadership & Technical Strategy",
          "Mobile AI Product Innovation"
        ]
      }
    ]
  },

  aiml: {
    title: "Advanced AI/ML Research & Innovation",
    description: "Deep dive into AI research, LLMs, and cutting-edge AI technologies for senior AI roles",
    icon: <Brain size={24} />,
    years: [
      {
        year: "Year 1",
        title: "AI Foundations & Research Skills",
        description: "Strong mathematical foundation with advanced AI programming",
        duration: "12 months",
        projects: "15+ Research-Grade AI Projects",
        skills: [
          "Python, C++, Julia for AI Development",
          "Advanced Statistics, Probability & Calculus",
          "Deep Learning Mathematics & Theory",
          "Advanced Data Structures & Algorithms",
          "PyTorch, TensorFlow, JAX Advanced",
          "Research Methodologies & Paper Analysis",
          "Classical ML to Modern Deep Learning",
          "Open Source AI Contribution"
        ]
      },
      {
        year: "Year 2",
        title: "Modern AI & Foundation Models",
        description: "Master state-of-the-art AI models and foundation technologies",
        duration: "12 months",
        projects: "20+ Cutting-Edge AI Systems",
        skills: [
          "Transformer Architecture & Attention Mechanisms",
          "Large Language Models (GPT, Claude, Gemini)",
          "Computer Vision (ViTs, CLIP, DALL-E)",
          "Multimodal AI (Vision + Language + Audio)",
          "Generative AI (Diffusion Models, GANs)",
          "Reinforcement Learning & RLHF",
          "Advanced MLOps & Model Deployment",
          "AI Safety & Alignment Research"
        ]
      },
      {
        year: "Year 3",
        title: "AI Innovation & Industry Leadership",
        description: "Lead AI research and develop next-generation AI solutions",
        duration: "12 months",
        projects: "25+ Breakthrough AI Innovations",
        skills: [
          "Foundation Model Pre-training & Fine-tuning",
          "Advanced AI Agent Development",
          "Distributed Training & Model Parallelism",
          "AI Hardware Optimization (GPUs, TPUs)",
          "Research Paper Writing & Publication",
          "AI Product Strategy & Vision",
          "Industry Collaboration & Patents",
          "Next-Gen AI Architecture Innovation"
        ]
      }
    ]
  },
  iot: {
    title: "AI-Powered IoT & Edge Computing",
    description: "Build intelligent IoT ecosystems with AI, edge computing, and future connectivity",
    icon: <Cpu size={24} />,
    years: [
      {
        year: "Year 1",
        title: "Smart IoT Fundamentals",
        description: "Build intelligent connected devices with embedded AI",
        duration: "12 months",
        projects: "12+ AI-Enhanced IoT Projects",
        skills: [
          "C/C++, Python for IoT & Embedded AI",
          "Arduino, Raspberry Pi & AI Accelerators",
          "Sensor Fusion & Intelligent Data Processing",
          "Edge AI & TinyML Implementation",
          "IoT Protocols (MQTT, CoAP, 5G)",
          "Wireless Communication & Networking",
          "Real-time Data Analytics",
          "IoT Security & Privacy"
        ]
      },
      {
        year: "Year 2",
        title: "Industrial IoT & AI Integration",
        description: "Master enterprise IoT with advanced AI capabilities",
        duration: "12 months",
        projects: "18+ Industrial AI-IoT Solutions",
        skills: [
          "Industrial IoT (IIoT) & Smart Manufacturing",
          "Edge Computing & Fog Computing",
          "AI/ML for Predictive Maintenance",
          "Digital Twins & Simulation",
          "5G/6G Networks & Edge AI",
          "Blockchain for IoT Security",
          "Cloud IoT Platforms (AWS, Azure, Google)",
          "IoT Data Analytics & Visualization"
        ]
      },
      {
        year: "Year 3",
        title: "Future IoT & Smart City Innovation",
        description: "Lead next-generation IoT with AI and emerging technologies",
        duration: "12 months",
        projects: "25+ Smart City & Future IoT Systems",
        skills: [
          "Smart City Infrastructure & AI",
          "Autonomous Systems & Robotics Integration",
          "Quantum IoT & Advanced Connectivity",
          "Metaverse & Extended Reality IoT",
          "Sustainable IoT & Green Computing",
          "Advanced IoT Architecture & Leadership",
          "Innovation Strategy & Future Tech",
          "IoT Entrepreneurship & Product Development"
        ]
      }
    ]
  }
};

const CareerPaths = () => {
  const [selectedPath, setSelectedPath] = useState('aiready');
  const navigate = useNavigate();

  const currentPath = careerPaths[selectedPath];

  const handleEnrollClick = () => {
    navigate('/contact', { state: { program: currentPath.title } });
  };

  return (
    <CareerPathsSection>
      <Container>
        <SectionHeader>
          <Badge className="badge">
            <Calendar size={16} />
            AI-Powered Career Programs 2025
          </Badge>
          <h1>Master Future-Ready Tech Skills with AI Integration</h1>
          <p className="subtitle">
            Choose from our AI-enhanced career paths: Mobile, .NET, Full-Stack, IoT - all designed to make you irreplaceable in the AI era!
          </p>
        </SectionHeader>

        <PathSelector>
          <PathTab 
            active={selectedPath === 'aiready'} 
            onClick={() => setSelectedPath('aiready')}
          >
            <Target size={20} />
            AI-Ready Job (18M)
          </PathTab>
          <PathTab 
            active={selectedPath === 'fullstack'} 
            onClick={() => setSelectedPath('fullstack')}
          >
            <Code size={20} />
            AI-Enhanced Full-Stack
          </PathTab>
          <PathTab 
            active={selectedPath === 'mobile'} 
            onClick={() => setSelectedPath('mobile')}
          >
            <Smartphone size={20} />
            AI-Mobile Development
          </PathTab>
          <PathTab 
            active={selectedPath === 'dotnet'} 
            onClick={() => setSelectedPath('dotnet')}
          >
            <Settings size={20} />
            AI-.NET Enterprise
          </PathTab>
          <PathTab 
            active={selectedPath === 'aiml'} 
            onClick={() => setSelectedPath('aiml')}
          >
            <Brain size={20} />
            AI/ML Research
          </PathTab>
          <PathTab 
            active={selectedPath === 'iot'} 
            onClick={() => setSelectedPath('iot')}
          >
            <Cpu size={20} />
            AI-IoT & Edge
          </PathTab>
        </PathSelector>

        <PathContent>
          <PathHeader>
            <h2>{currentPath.title}</h2>
            <p>{currentPath.description}</p>
          </PathHeader>

          <YearGrid>
            {currentPath.years.map((yearData, index) => (
              <YearCard key={index}>
                <div className="year-badge">{yearData.year}</div>
                <div className="year-header">
                  <h3>{yearData.title}</h3>
                  <p>{yearData.description}</p>
                </div>

                <div className="year-stats">
                  <div className="stat">
                    <span className="value">{yearData.duration}</span>
                    <span className="label">Duration</span>
                  </div>
                  <div className="stat">
                    <span className="value">{yearData.projects}</span>
                    <span className="label">Projects</span>
                  </div>
                  <div className="stat">
                    <span className="value">{yearData.skills.length}</span>
                    <span className="label">Skills</span>
                  </div>
                </div>

                <div className="skills-list">
                  <h4>What You'll Learn:</h4>
                  <ul>
                    {yearData.skills.map((skill, idx) => (
                      <li key={idx}>
                        <CheckCircle className="check-icon" />
                        {skill}
                      </li>
                    ))}
                  </ul>
                </div>
              </YearCard>
            ))}
          </YearGrid>
        </PathContent>

        <CtaSection>
          <h2>Ready to Start Your Journey?</h2>
          <p>Join thousands of students who have successfully transformed their careers with our programs</p>
          <div className="cta-buttons">
            <WhiteButton onClick={handleEnrollClick}>
              <Users size={16} />
              Enroll Now
            </WhiteButton>
            <WhiteButton onClick={() => navigate('/contact')}>
              <Target size={16} />
              Get Career Guidance
            </WhiteButton>
          </div>
        </CtaSection>
      </Container>
    </CareerPathsSection>
  );
};

export default CareerPaths;
